// src/pages/DashboardLayout.jsx

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useActiveMeeting } from '../context/ActiveMeetingContext';
import { useAuthorizedAccess } from '../hooks/useAuthorizedAccess';
import useHRToolsAccess from '../hooks/useHRToolsAccess';
// ✨ 1. Import new icons
import { FaTachometerAlt, FaUsersCog, FaSignOutAlt, FaBars, FaTimes, FaArchive, FaBookOpen, FaCogs, FaChevronLeft, FaChevronRight, FaAngleDown, FaEnvelope, FaWhatsapp, FaUsers, FaFileContract, FaUserShield, FaIdCard, FaInfoCircle, FaChartBar, FaExternalLinkAlt, FaToolbox, FaSlideshare } from 'react-icons/fa';
import JitsiPortal from '../components/JitsiPortal';
import './DashboardLayout.css'; // We will add CSS to this

const DashboardLayout = () => {
    const { userData, logout } = useAuth();
    const { hasAccess: hasEmployeeListAccess } = useAuthorizedAccess();
    const { hasAccess: hasHRToolsAccess } = useHRToolsAccess();
    const navigate = useNavigate();
    const location = useLocation();
    const onMeetPage = location.pathname.startsWith('/dashboard/meet');
    const onSlideCreatorPage = location.pathname.startsWith('/dashboard/slide-creator');
    const { activeMeeting } = useActiveMeeting();
    // Portal is in full-screen mode when there's an active meeting on the meet page
    const portalIsFullScreen = onMeetPage && !!activeMeeting;

    const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [isMenuCollapsed, setMenuCollapsed] = useState(false);
    // ✨ 2. Add state for our new dropdown
    const [isMarketingOpen, setMarketingOpen] = useState(false);
    const [isHROpen, setHROpen] = useState(false);

    const hrBtnRef = useRef(null);
    const mktBtnRef = useRef(null);
    const hrDropdownRef = useRef(null);
    const mktDropdownRef = useRef(null);
    const hrFlyoutRef = useRef(null);
    const mktFlyoutRef = useRef(null);
    const [hrMenuTop, setHRMenuTop] = useState(0);
    const [marketingMenuTop, setMarketingMenuTop] = useState(0);

    // Close flyouts when sidebar is expanded
    useEffect(() => {
        if (!isMenuCollapsed) {
            setHROpen(false);
            setMarketingOpen(false);
        }
    }, [isMenuCollapsed]);

    // Keep body dataset in sync so JitsiPortal CSS can offset by sidebar width
    useEffect(() => {
        document.body.dataset.sidebarCollapsed = isMenuCollapsed ? 'true' : 'false';
        return () => { delete document.body.dataset.sidebarCollapsed; };
    }, [isMenuCollapsed]);

    // In expanded mode, keep the correct accordion open for the active route
    useEffect(() => {
        if (isMenuCollapsed) return;
        const hrRoutes = ['/dashboard/employee-list', '/dashboard/offer-letter', '/dashboard/id-card', '/dashboard/prrepx-standard', '/dashboard/manage-access'];
        const mktRoutes = ['/dashboard/email-portal', '/dashboard/whatsapp-portal'];
        if (hrRoutes.some(p => location.pathname.startsWith(p))) setHROpen(true);
        if (mktRoutes.some(p => location.pathname.startsWith(p))) setMarketingOpen(true);
    }, [location.pathname, isMenuCollapsed]);

    // Close on outside click — check against the whole dropdown <li> wrapper AND the portal flyout
    useEffect(() => {
        if (!isHROpen && !isMarketingOpen) return;
        const handler = (e) => {
            if (isHROpen
                && hrDropdownRef.current && !hrDropdownRef.current.contains(e.target)
                && hrFlyoutRef.current  && !hrFlyoutRef.current.contains(e.target)) {
                setHROpen(false);
            }
            if (isMarketingOpen
                && mktDropdownRef.current && !mktDropdownRef.current.contains(e.target)
                && mktFlyoutRef.current  && !mktFlyoutRef.current.contains(e.target)) {
                setMarketingOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isHROpen, isMarketingOpen]);

    const handleHRToggle = (e) => {
        e.stopPropagation();
        if (isMenuCollapsed && hrBtnRef.current) {
            setHRMenuTop(hrBtnRef.current.getBoundingClientRect().top);
        }
        setHROpen(v => !v);
    };

    const handleMarketingToggle = (e) => {
        e.stopPropagation();
        if (isMenuCollapsed && mktBtnRef.current) {
            setMarketingMenuTop(mktBtnRef.current.getBoundingClientRect().top);
        }
        setMarketingOpen(v => !v);
    };

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    if (!userData) {
        return <div className="loading-state">Loading user data...</div>;
    }

    const hasPersonalDashboard = userData.role === 'admin' || userData.role === 'employee';
    const hasManagementPortal = userData.role === 'admin' || userData.role === 'superadmin';
    const isSuperAdmin = userData.role === 'superadmin';
    const hasOfferLetterAccess = userData.role === 'superadmin' || userData.offerLetterAccess === true;
    const hasIdCardAccess = userData.role === 'superadmin' || userData.idCardAccess === true;
    const canOpenHRTools = userData.role === 'superadmin' || hasHRToolsAccess;
    const isLoggedIn = !!userData;

    if (userData.role === 'superadmin' && (location.pathname === '/dashboard' || location.pathname === '/dashboard/')) {
        navigate('/dashboard/manage', { replace: true });
        return <div className="loading-state">Redirecting...</div>;
    }

    const closeMobileSidebar = () => {
        setMobileSidebarOpen(false);
        setHROpen(false);
        setMarketingOpen(false);
    };

    // Menu item lists — shared between accordion and portal flyout
    const hrMenuItems = (
        <>
            {hasEmployeeListAccess && (
                <li>
                    <NavLink to="/dashboard/employee-list" onClick={closeMobileSidebar}>
                        <FaUsers /><span>Employee Directory</span>
                    </NavLink>
                </li>
            )}
            {hasOfferLetterAccess && (
                <li>
                    <NavLink to="/dashboard/offer-letter" onClick={closeMobileSidebar}>
                        <FaFileContract /><span>Offer Letter Generator</span>
                    </NavLink>
                </li>
            )}
            {hasIdCardAccess && (
                <li>
                    <NavLink to="/dashboard/id-card" onClick={closeMobileSidebar}>
                        <FaIdCard /><span>ID Card Generator</span>
                    </NavLink>
                </li>
            )}
            {canOpenHRTools && (
                <li>
                    <NavLink to="/dashboard/prrepx-standard" onClick={closeMobileSidebar}>
                        <FaToolbox /><span>PrrepX Standard</span>
                    </NavLink>
                </li>
            )}
            {isSuperAdmin && (
                <li>
                    <NavLink to="/dashboard/manage-access" onClick={closeMobileSidebar}>
                        <FaUserShield /><span>Access Management</span>
                    </NavLink>
                </li>
            )}
            <li>
                <a href="https://prrepxtools.vercel.app/" target="_blank" rel="noopener noreferrer" onClick={closeMobileSidebar}>
                    <FaExternalLinkAlt /><span>PrrepX Tools</span>
                </a>
            </li>
        </>
    );

    const marketingMenuItems = (
        <>
            <li>
                <NavLink to="/dashboard/email-portal" onClick={closeMobileSidebar}>
                    <FaEnvelope /><span>Email Portal</span>
                </NavLink>
            </li>
            <li>
                <NavLink to="/dashboard/whatsapp-portal" onClick={closeMobileSidebar}>
                    <FaWhatsapp /><span>WhatsApp Portal</span>
                </NavLink>
            </li>
        </>
    );

    const navLinks = (
        <nav className="sidebar-nav">
            <ul>
                {/* 1. My Portal — personal dashboard (employee / admin) */}
                {hasPersonalDashboard && (
                    <li>
                        <NavLink to="/dashboard/my-portal" onClick={closeMobileSidebar} data-tooltip={isMenuCollapsed ? "My Portal" : undefined}>
                            <FaTachometerAlt /><span>My Portal</span>
                        </NavLink>
                    </li>
                )}
                {/* 3 & 4. Management + Experience Records (admin / superadmin) */}
                {hasManagementPortal && (
                    <>
                        <li>
                            <NavLink to="/dashboard/manage" onClick={closeMobileSidebar} data-tooltip={isMenuCollapsed ? "Management" : undefined}>
                                <FaUsersCog /><span>Management</span>
                            </NavLink>
                        </li>
                        <li>
                            <NavLink to="/dashboard/experience-records" onClick={closeMobileSidebar} data-tooltip={isMenuCollapsed ? "Experience Records" : undefined}>
                                <FaArchive /><span>Experience Records</span>
                            </NavLink>
                        </li>
                    </>
                )}
                {/* 5. HR Tools dropdown */}
                {(hasOfferLetterAccess || hasEmployeeListAccess || hasIdCardAccess || canOpenHRTools) && (
                    <li ref={hrDropdownRef} className={`dropdown ${isHROpen ? 'open' : ''}`}>
                        <button
                            ref={hrBtnRef}
                            className="dropdown-toggle"
                            onClick={handleHRToggle}
                            data-tooltip={isMenuCollapsed ? "HR Tools" : undefined}
                        >
                            <FaFileContract />
                            <span>HR Tools</span>
                            <FaAngleDown className="dropdown-arrow" />
                        </button>
                        <ul className="dropdown-menu">{hrMenuItems}</ul>
                        {isMenuCollapsed && isHROpen && ReactDOM.createPortal(
                            <ul ref={hrFlyoutRef} className="sidebar-flyout-portal" style={{ top: hrMenuTop }} data-label="HR Tools">
                                {hrMenuItems}
                            </ul>,
                            document.body
                        )}
                    </li>
                )}
                {/* 7. Marketing dropdown (superadmin only) */}
                {isSuperAdmin && (
                    <li ref={mktDropdownRef} className={`dropdown ${isMarketingOpen ? 'open' : ''}`}>
                        <button
                            ref={mktBtnRef}
                            className="dropdown-toggle"
                            onClick={handleMarketingToggle}
                            data-tooltip={isMenuCollapsed ? "Marketing" : undefined}
                        >
                            <FaCogs />
                            <span>Marketing</span>
                            <FaAngleDown className="dropdown-arrow" />
                        </button>
                        <ul className="dropdown-menu">{marketingMenuItems}</ul>
                        {isMenuCollapsed && isMarketingOpen && ReactDOM.createPortal(
                            <ul ref={mktFlyoutRef} className="sidebar-flyout-portal" style={{ top: marketingMenuTop }} data-label="Marketing">
                                {marketingMenuItems}
                            </ul>,
                            document.body
                        )}
                    </li>
                )}
                {/* 9. Employee Manual — reference */}
                {isLoggedIn && (
                    <li>
                        <NavLink to="/dashboard/weekly-progress" onClick={closeMobileSidebar} data-tooltip={isMenuCollapsed ? "Weekly Progress" : undefined}>
                            <FaChartBar /><span>Weekly Progress</span>
                        </NavLink>
                    </li>
                )}
                {isLoggedIn && (
                    <li>
                        <NavLink to="/dashboard/employee-manual" onClick={closeMobileSidebar} data-tooltip={isMenuCollapsed ? "Employee Manual" : undefined}>
                            <FaBookOpen /><span>Employee Manual</span>
                        </NavLink>
                    </li>
                )}
                {isLoggedIn && (
                    <li>
                        <NavLink to="/dashboard/slide-creator" onClick={closeMobileSidebar} data-tooltip={isMenuCollapsed ? "Slide Creator" : undefined}>
                            <FaSlideshare /><span>Slide Creator</span>
                        </NavLink>
                    </li>
                )}
                {/* 9. About PrrepX — at bottom */}
                {isLoggedIn && (
                    <li>
                        <NavLink to="/dashboard/about" onClick={closeMobileSidebar} data-tooltip={isMenuCollapsed ? "About PrrepX" : undefined}>
                            <FaInfoCircle /><span>About PrrepX</span>
                        </NavLink>
                    </li>
                )}
            </ul>
        </nav>
    );

    return (
        <div className={`dashboard-layout ${isMenuCollapsed ? 'collapsed' : ''}`}>
            <div
                className={`sidebar-overlay ${isMobileSidebarOpen ? 'is-active' : ''}`}
                onClick={closeMobileSidebar}
            ></div>

            <aside className={`dashboard-sidebar ${isMobileSidebarOpen ? 'is-open' : ''}`}>
                <div className="mobile-sidebar-header">
                    <h4>Menu</h4>
                    <button onClick={closeMobileSidebar} className="close-sidebar-btn" data-tooltip="Close Menu"><FaTimes /></button>
                </div>
                
                <div className="sidebar-profile">
                    <button 
                        className="desktop-menu-toggle" 
                        onClick={() => setMenuCollapsed(!isMenuCollapsed)}
                    >
                        {isMenuCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
                    </button>
                    <img src={userData.photoURL || '/default-avatar.png'} alt="Profile" className="sidebar-avatar" />
                    <div className="profile-info">
                        <h3>{userData.name}</h3>
                        <p className={`role-badge ${userData.role}`}>{userData.role}</p>
                    </div>
                </div>

                {navLinks}
                
                <div className="sidebar-footer">
                    <button onClick={handleLogout} className="logout-button">
                        <FaSignOutAlt />
                        <span>Logout</span>
                    </button>
                </div>
            </aside>

            <main className={`dashboard-content${onMeetPage ? ' meet-content' : ''}${onSlideCreatorPage ? ' slide-creator-content' : ''}`}>
                {/* Persistent PiP meeting portal — mounts to document.body, survives navigation */}
                <JitsiPortal />
                {!portalIsFullScreen && (
                  <div className="mobile-menu-bar">
                    <button className="mobile-nav-toggle" onClick={() => setMobileSidebarOpen(true)} data-tooltip="Open Menu">
                        <FaBars />
                        <span>Menu</span>
                    </button>
                  </div>
                )}
                <Outlet />
            </main>
        </div>
    );
};

export default DashboardLayout;